/**
 * /research/combine-filter — Combine & Filter (prototype v39
 * `#page-combine-filter`). Pick existing people lists (capped at
 * `MAX_COMBINE_SOURCES`, never shown — trying to add one more raises a
 * warning toast), watch a live
 * funnel + a paged preview update, optionally filter (Title / Company /
 * Location, and since task 2026-09-24-combine-more-filters Past company /
 * Past title / Keyword / Time in role / Connection / Email and "In at least
 * N lists"), pick the target Thrive search (inherited when every source
 * shares one), untick anyone unwanted, and Generate a new people list. A
 * SINGLE source list is valid (product decision, 2026-09-23) — it is a
 * legitimate way to just FILTER one list, not only to combine several;
 * "Only people in every list" still needs 2+ (meaningless with one) and
 * stays disabled with a tooltip saying so.
 *
 * Behind the `combine-filter` flag (released on every stage 2026-10-07; it was
 * phase 3 of the preview ladder until then) — see `routing/routes.tsx`'s
 * `PAGE_OVERRIDES` entry for the gate itself; this component renders
 * unconditionally once reached.
 *
 * `useCombinePreview` (debounced 300 ms, keyed by the whole recipe) drives
 * both the funnel and the table; `useCombineGenerate` posts the recipe and
 * navigates to `/research/imports/:id` on success. No Compare starts there:
 * the new list inherits its sources' Thrive results (epic
 * 2026-10-01-combine-stability-perf, sub-task 01).
 *
 * UI-parity pass (2026-09-23, orchestrator addendum): single-column card
 * layout (People lists → Filters → Review contacts → Result → a footer
 * with the title + Generate), matching the mock rather than the earlier
 * two-column grid with a sidebar. "Start over" (the page header) resets
 * every piece of state this component owns AND every child's own local
 * state, via `resetKey` — bumping it remounts `SourcePicker` / `FilterBuilder`
 * / `PreviewTable`, whose in-progress rows / search text / view toggle have
 * no counterpart in this component's own state to reset by hand.
 *
 * Bug hunt 2026-09-23: the preview is shown only for the lists selected now
 * (F1); `page` goes back to 1 whenever the recipe or the review tab changes
 * (F2); the "Removed by you" tab is its own server-side view (F3/F4);
 * unticks are cleared when the source set changes (F5); Compare with
 * Thrive runs only where a run can make a list count as compared (F6,
 * `compareRows.ts`; since task 2026-09-24-combine-more-filters it starts by
 * itself, `useAutoCompare.ts`, with no button); "Only people in every list" is sent only with 2+
 * lists (F7; since task 2026-09-24-combine-more-filters the rule is "In at
 * least N lists", `steps/listRule.ts`); Generate waits for the preview and for any running Compare,
 * and cannot double-submit (F9); a 422 says "Pick a search." only when it is
 * about the search (F13).
 *
 * /pr-review iter 2: a request over the UTF-8 byte budget is never sent and
 * says so (`requestBudget.ts`), as does a bodiless 403 from the edge; the
 * 413 copy names the limit from the error's own structured detail; Company /
 * Location suggestions come from every source row (`filter_suggestions`);
 * an untick the server reports as stale is dropped with a notice; an
 * abandoned Compare no longer holds up Generate (`compareRows.ts`).
 *
 * Epic 2026-10-01-combine-stability-perf, sub-task 02 (Jose's QA): the
 * target search is set only by the recruiter (the lists' shared search is a
 * suggestion with "Use this search"), so nothing reaches Thrive and no
 * request carries a search before that; each list's state is read from a
 * preview for the CURRENT target only (`statesForTarget`), so adding a list
 * or changing the target never shows another target's answer; a list is
 * auto-compared once per target, not once per session; Generate waits, and
 * says why, while any list is not checked; progress counts lists.
 *
 * Tokenized — design-system token utilities only, no inline `style={{}}`,
 * no arbitrary `text-[Npx]`.
 */
import { useMe } from "@nu-assist/shared-fe";
import { Button, SegmentedControl } from "@nu-assist/ui";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import type {
  CombineConflictCode,
  CombinePreviewView,
  CombineSourceMembershipError,
} from "@nu-assist/contracts";

import {
  type CombineFilter,
  type CombinePreviewRequest,
  type ImportSummary,
  ImportsApiError,
  type ImportsClient,
  createImportsClient,
} from "../../../clients/imports.client";
import { useCompareSources } from "../../../features/imports/useCompareSources";
import { type ContactsTableSort, NAME_SORT_KEY } from "../import-detail/ContactsTable";
import { displayTitle } from "../import-detail/importTitle";
import { InfoTip } from "./InfoTip";
import { SelectedListsPanel } from "./SelectedListsPanel";
import {
  type CompareRow,
  MEMBERSHIP_ERROR_RETRY,
  NO_SEEN_CHECKING,
  type SeenChecking,
  activeTargetCheckIds,
  anyCompareInFlight,
  anyTargetCheckRetrying,
  autoCompareIds,
  checkProgress,
  checkedAfterCompare,
  compareHeading,
  compareRows,
  failedCheckIds,
  generateHold,
  generateNote,
  idsSeenChecking,
  nextAllCheckedKey,
  nextSeenChecking,
  nextTargetCheckRetryAt,
  pendingTargetChecks,
  retryClockLabel,
  stillCheckingCopy,
} from "./compareRows";
import {
  MAX_COMBINE_BODY_BYTES,
  REQUEST_ID_PLACEHOLDER,
  REQUEST_TOO_LARGE,
  exceedsBodyBudget,
  isEdgeRejection,
} from "./requestBudget";
import { FilterBuilder, type StageSource } from "./steps/FilterBuilder";
import { PreviewFunnel } from "./steps/PreviewFunnel";
import { PreviewTable } from "./steps/PreviewTable";
import { MAX_COMBINE_SOURCES, SourcePicker } from "./steps/SourcePicker";
import { TargetSearchStep } from "./steps/TargetSearchStep";
import { type ListRule, applyListRule } from "./steps/listRule";
import { useAutoCompare } from "./useAutoCompare";
import { GenerateUnconfirmedError, useCombineGenerate } from "./useCombineGenerate";
import { useCombinePreview } from "./useCombinePreview";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";
const PREVIEW_PAGE_SIZE = 20;
/** Same WAF-driven cap the API enforces on `CombineRecipe.excluded_contact_ids`.
 *  Lowered 150 -> 100 in the iter-1 review fix round (PR #962): 150 UUIDs
 *  alone left no headroom for a filter's own values under the shared ALB's
 *  8 KB body cap once `CombineFilterClause.values` is considered too — see
 *  `FilterBuilder`'s own per-clause / per-value caps. */
const MAX_EXCLUSIONS = 100;
/** 2 -> 1 (product decision, 2026-09-23): filtering a SINGLE list is a
 *  legitimate use of this page, not only combining several. */
const MIN_SOURCES = 1;

// One client per app session — same pattern every other Imports route uses
// (`ImportsPage.tsx`), so MSW's module-scope interception still applies.
const defaultClient = createImportsClient({ apiBase: API_BASE });

export interface CombineFilterPageProps {
  /** Injectable for tests (defaults to the module-shared typed client). */
  client?: ImportsClient;
  /** Injectable caller email for tests; defaults to `useMe()`. */
  meEmailOverride?: string;
  /** The UTF-8 byte budget for a request body — `MAX_COMBINE_BODY_BYTES`
   *  (the API's own limit) unless a test injects a smaller one. */
  maxBodyBytes?: number;
  /** How long the selection must stay still before Compare starts by itself
   *  — `AUTO_COMPARE_DELAY_MS` unless a test injects a shorter one. */
  autoCompareDelayMs?: number;
  /** How often the page reads the preview again while a list is being
   *  checked against the target search — `TARGET_CHECK_POLL_MS` unless a
   *  test injects a shorter one. */
  targetCheckPollMs?: number;
  /** How often the page reads the preview again while a list's check
   *  against the target search has FAILED — `TARGET_CHECK_RETRY_POLL_MS`
   *  unless a test injects a shorter one. */
  targetCheckRetryPollMs?: number;
  /** How long a check has been pending before the page backs off from the
   *  fast pace to `targetCheckBackoffPollMs` — `TARGET_CHECK_BACKOFF_AFTER_MS`
   *  unless a test injects a shorter one. */
  targetCheckBackoffAfterMs?: number;
  /** The backed-off pace — `TARGET_CHECK_BACKOFF_POLL_MS` unless a test
   *  injects a shorter one. */
  targetCheckBackoffPollMs?: number;
  /** How long a check has been pending before the page reads at
   *  `targetCheckSlowPollMs` — `TARGET_CHECK_SLOW_AFTER_MS` unless a test
   *  injects a shorter one. */
  targetCheckSlowAfterMs?: number;
  /** The slowest pace (never a stop) — `TARGET_CHECK_SLOW_POLL_MS` unless a
   *  test injects a shorter one. */
  targetCheckSlowPollMs?: number;
  /** How long a check has been pending before the page says it is taking
   *  longer than expected (it keeps reading) —
   *  `TARGET_CHECK_SLOW_NOTE_AFTER_MS` unless a test injects a shorter one. */
  targetCheckSlowNoteAfterMs?: number;
  /** While a failed check is read again (`targetCheckRetryPollMs`), how long
   *  the wait has lasted before the page backs off to
   *  `targetCheckRetryBackoffPollMs` — `TARGET_CHECK_RETRY_BACKOFF_AFTER_MS`
   *  unless a test injects a shorter one. */
  targetCheckRetryBackoffAfterMs?: number;
  /** `TARGET_CHECK_RETRY_BACKOFF_POLL_MS` unless a test injects another. */
  targetCheckRetryBackoffPollMs?: number;
  /** Past this the retry reading is `targetCheckRetrySlowPollMs` —
   *  `TARGET_CHECK_RETRY_SLOW_AFTER_MS` unless a test injects another. */
  targetCheckRetrySlowAfterMs?: number;
  /** `TARGET_CHECK_RETRY_SLOW_POLL_MS` unless a test injects another. */
  targetCheckRetrySlowPollMs?: number;
  /** How long a re-read of the CURRENT recipe must be in flight before the
   *  page says "Updating preview…" — `UPDATING_PREVIEW_DELAY_MS` unless a
   *  test injects another. */
  updatingPreviewDelayMs?: number;
}

/** Past this long of waiting on one check, the page reads at the slower
 *  `TARGET_CHECK_BACKOFF_POLL_MS` (/pr-review iter 1, Agent A: an open tab
 *  polled every 4 s with no ceiling). */
const TARGET_CHECK_BACKOFF_AFTER_MS = 2 * 60_000;
const TARGET_CHECK_BACKOFF_POLL_MS = 15_000;
/** Past this long, one read a minute. The page NEVER stops reading while a
 *  check is pending (/pr-review iter 2, Agent A N2 + Agent C N2): the server
 *  caps a pending check at 15 minutes from its first request (ADR-0027) and
 *  applies the cap only when a preview or Generate request ARRIVES, so a page
 *  that stopped reading would leave Generate disabled for ever. */
const TARGET_CHECK_SLOW_AFTER_MS = 10 * 60_000;
const TARGET_CHECK_SLOW_POLL_MS = 60_000;
/** The worst case a check stays pending: the 900 s cap, plus a worker that is
 *  mid-read at the cap (~1260 s in all). Past this the page says the check
 *  is taking longer than expected — and keeps reading. */
const TARGET_CHECK_SLOW_NOTE_AFTER_MS = 21 * 60_000;
/** A re-read of the current recipe shorter than this is not worth a label:
 *  focus refetches and polls answer from the kept merge in milliseconds. */
const UPDATING_PREVIEW_DELAY_MS = 400;

/** The server starts a failed check again on the first preview at or after
 *  its `membership_retry_at` (a back-off from 120 s to 1800 s, ADR-0027 §3),
 *  and a late worker can still adopt a `worker_timeout` one before that.
 *  Generate does not wait on a failed check, but the row heals by itself, so
 *  the page keeps reading a preview at this slower pace while a retry is
 *  left, and wakes near the earliest `membership_retry_at`
 *  (`nextTargetCheckRetryAt`), never later than the back-off below. It
 *  stops once every failed check reads `membership_retry_at` null (out of
 *  retries) or can only heal by a new Compare (`search_not_found`,
 *  `source_not_compared`): nothing a read could show. A target the API's
 *  searches list does not know yet (`isUnknownTargetSearch`) is read again
 *  at this pace too: a later preview checks it once the list syncs. */
const TARGET_CHECK_RETRY_POLL_MS = 30_000;
/** Read this long after a `membership_retry_at`, so the server's clock has
 *  passed it when the preview arrives. */
const TARGET_CHECK_RETRY_SLACK_MS = 2_000;
/** Reading a failed check again backs off like a pending one (/pr-review
 *  iter 3 on PR #993, Agent A N5 / Agent C R2): an open tab on a source
 *  whose failure keeps coming back (a retryable code, a lost worker) must not
 *  poll every 30 s for ever. Never a stop — a late adopt still shows. */
const TARGET_CHECK_RETRY_BACKOFF_AFTER_MS = 10 * 60_000;
const TARGET_CHECK_RETRY_BACKOFF_POLL_MS = 60_000;
const TARGET_CHECK_RETRY_SLOW_AFTER_MS = 30 * 60_000;
const TARGET_CHECK_RETRY_SLOW_POLL_MS = 5 * 60_000;

/** The target search the recruiter CONFIRMED: picked in the search field,
 *  or the lists' shared search taken with "Use this search". */
interface TargetSearch {
  id: string;
  name: string | null;
}

/** "Q3 Search" / "search 4414" — how copy names the target. */
function targetLabelOf(target: TargetSearch): string {
  return target.name ?? `search ${target.id}`;
}

/** While the server checks a list against the target search in the
 *  background (`membership_check="pending"`, epic
 *  2026-10-01-combine-stability-perf), the page reads the preview again this
 *  often until the check settles. The check is one Thrive read of the
 *  target search (~20 s for a search of 3,500 candidates), so a few seconds
 *  between reads is enough; the preview answers from the kept merge when
 *  nothing changed. */
const TARGET_CHECK_POLL_MS = 4_000;

/** A check pending past the server's worst case (the page keeps reading). */
const CHECK_TAKING_LONG =
  "Still checking against the target search — this is taking longer than expected. The page keeps checking.";

/** Why a list could not be checked against the target search
 *  (`CombineSourceState.membership_error`), in the recruiter's words. */
function targetCheckFailure(error: CombineSourceMembershipError | null): string {
  switch (error) {
    case "thrive_timeout":
      return "Thrive took too long to list the target search's candidates";
    case "thrive_rate_limited":
      return "Thrive is busy";
    case "search_not_found":
      return "Thrive has no such search";
    case "source_not_compared":
      return "this list's Compare is not complete";
    case "dispatch_failed":
      return "the check could not be started";
    case "thrive_error":
      return "Thrive returned an error";
    case "worker_timeout":
      return "the check took too long";
    default:
      // Any code this build does not know yet (the API may add some).
      return "the check could not be completed";
  }
}

/** One list's Compare status in the People-lists card (`compareRows`).
 *  `targetLabel` names the confirmed target (`null` with none);
 *  `suggested` says whether the lists' shared search waits to be
 *  confirmed. */
function compareNote(row: CompareRow, targetLabel: string | null, suggested: boolean): string {
  switch (row.kind) {
    case "evaluating":
      return targetLabel ? `Checking against ${targetLabel}…` : "Reading its Thrive status…";
    case "pending":
      return "Starting Compare…";
    case "comparing":
      return "Comparing in Thrive…";
    case "compared":
      return "Compared · today";
    case "failed":
      return "Compare failed";
    case "unchecked":
      return "Not checked in Thrive";
    case "no_target":
      return suggested
        ? "Confirm the target search to check it"
        : "Pick a target search to check it";
    case "target_checking":
      return `Checking against ${targetLabel ?? "the target search"}…`;
    case "target_check_failed": {
      if (row.unknownSearch) {
        return `Check failed — ${targetLabel ?? "the target search"} isn't available yet. Pick another search or try again shortly.`;
      }
      const why = `Check failed — ${targetCheckFailure(row.error ?? null)}`;
      // When it runs again is the server's to say (`membership_retry_at`,
      // /pr-review iter 6 on PR #993, Agent A N14 / Agent C I6-1): the
      // back-off grows per retry and stops after the last one.
      if (row.retryAt) return `${why}. It's checked again around ${retryClockLabel(row.retryAt)}.`;
      if (row.error === "source_not_compared") return `${why}. Compare it again to check it.`;
      if (row.error != null && (MEMBERSHIP_ERROR_RETRY[row.error] ?? "never") !== "never") {
        const late =
          row.error === "worker_timeout"
            ? "; a late result shows when you return to this page"
            : "";
        return `${why}. It won't be retried automatically. Compare the list again to re-check it${late}.`;
      }
      return why;
    }
    default: {
      // A new kind fails `tsc` here instead of rendering nothing.
      const unhandled: never = row;
      return unhandled;
    }
  }
}

/** "Sep 23, 2026" — the mock's `COMBINE_TODAY_LABEL`, not an ISO date. */
function todayLabel(): string {
  return new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function CardHeader({
  title,
  tip,
  children,
}: {
  title: string;
  tip: string;
  children?: ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <h2 className="text-h3 text-text">{title}</h2>
      <InfoTip content={tip} />
      {children ? <div className="ml-auto flex items-center gap-2">{children}</div> : null}
    </div>
  );
}

// `ContactsTable`'s own sort keys (`NAME_SORT_KEY`, the plain string keys
// this page's own new columns use) -> `CombinePreviewRequest.sort_key`.
// Anything unrecognized falls back to "name" rather than sending a key the
// server would 422 on — defensive, since `onSort` is typed as a bare
// `(key: string) => void` and a future column could pass one this map does
// not (yet) know. Module-level (not a component method): it closes over no
// component state, so keeping it here is what lets `recipe`'s own `useMemo`
// below list every dependency it actually reads.
function toSortKey(key: string): NonNullable<CombinePreviewRequest["sort_key"]> {
  if (key === NAME_SORT_KEY) return "name";
  if (key === "company" || key === "position" || key === "location" || key === "thrive") {
    return key;
  }
  return "name";
}

/** A 422 is about the target search only when its message says so (the
 *  generate endpoint's "Thrive search ... does not exist"); any other 422 is
 *  a validation failure worth showing verbatim (bug hunt 2026-09-23, F13). */
function isSearchError(err: ImportsApiError): boolean {
  const text = [err.detail ?? "", ...(err.validationMessages ?? [])].join(" ");
  return /search/i.test(text);
}

function validationText(err: ImportsApiError): string {
  return err.detail ?? err.validationMessages?.join("; ") ?? "";
}

/** The one signal of the source cap: the cap itself is never shown (no
 *  "x / max" tally, no disabled rows), so trying to add a list past it is
 *  the only moment a recruiter learns of it. */
const MAX_SOURCES_REACHED = "You've reached the maximum number of lists for one combine.";

function notifyMaxSources() {
  toast.warning(MAX_SOURCES_REACHED);
}

/** The Compare-running reason, shared by the disabled-Generate hint and the
 *  generate endpoint's 409 for the same situation (F9). */
const WAITING_FOR_COMPARE = "Waiting for Compare to finish…";

/** The 503 both Combine endpoints answer when the request waited
 *  `Settings.combine_merge_wait_seconds` for the API's merge slot and got
 *  none (/pr-review iter 5). Nothing was read or written; retrying is safe. */
const PREVIEW_BUSY = "The preview is busy — try again in a moment.";
const GENERATE_BUSY = "Combine is busy — try again in a moment.";

/** Generate's answer was lost and its resend failed (`useCombineGenerate`):
 *  the list may exist. Trying again is safe — it reuses the same request id. */
const GENERATE_UNCONFIRMED =
  "Couldn't reach the server to confirm the new list. Generate again — it won't be created twice.";

/** The 413's limit, from the structured detail (`CombineOverLimitDetail`). */
function overLimitMax(err: ImportsApiError): number | null {
  const max = err.detailData?.max_source_contacts;
  return typeof max === "number" ? max : null;
}

/** The 409's `detail.code` (`CombineConflictDetail`), or `null` for any
 *  other error. The page branches on this, never on the wording of the
 *  message (/pr-review iter 3, Agent A §2 / Agent C m4); an unknown code
 *  falls through to the server's message. */
function conflictCode(err: unknown): CombineConflictCode | null {
  if (!(err instanceof ImportsApiError) || err.status !== 409) return null;
  const code = err.detailData?.code;
  return typeof code === "string" ? (code as CombineConflictCode) : null;
}

function overLimitCopy(max: number | null): string {
  return max != null
    ? `Too many contacts across the selected lists (max ${max.toLocaleString()}).`
    : "Too many contacts across the selected lists.";
}

export function CombineFilterPage({
  client,
  meEmailOverride,
  maxBodyBytes = MAX_COMBINE_BODY_BYTES,
  autoCompareDelayMs,
  targetCheckPollMs = TARGET_CHECK_POLL_MS,
  targetCheckRetryPollMs = TARGET_CHECK_RETRY_POLL_MS,
  targetCheckBackoffAfterMs = TARGET_CHECK_BACKOFF_AFTER_MS,
  targetCheckBackoffPollMs = TARGET_CHECK_BACKOFF_POLL_MS,
  targetCheckSlowAfterMs = TARGET_CHECK_SLOW_AFTER_MS,
  targetCheckSlowPollMs = TARGET_CHECK_SLOW_POLL_MS,
  targetCheckSlowNoteAfterMs = TARGET_CHECK_SLOW_NOTE_AFTER_MS,
  targetCheckRetryBackoffAfterMs = TARGET_CHECK_RETRY_BACKOFF_AFTER_MS,
  targetCheckRetryBackoffPollMs = TARGET_CHECK_RETRY_BACKOFF_POLL_MS,
  targetCheckRetrySlowAfterMs = TARGET_CHECK_RETRY_SLOW_AFTER_MS,
  targetCheckRetrySlowPollMs = TARGET_CHECK_RETRY_SLOW_POLL_MS,
  updatingPreviewDelayMs = UPDATING_PREVIEW_DELAY_MS,
}: CombineFilterPageProps = {}) {
  const me = useMe();
  const meEmail = meEmailOverride ?? (me.status === "authenticated" ? me.data.email : "");
  const activeClient = client ?? defaultClient;
  const queryClient = useQueryClient();

  // Bumped by "Start over" — remounting the children below (via `key`)
  // clears their own local state (in-progress filter rows, the source
  // picker's search/owner/tag, the review table's search/view) in one move,
  // rather than each needing an imperative reset method threaded down.
  const [resetKey, setResetKey] = useState(0);

  const [sources, setSources] = useState<Map<string, ImportSummary>>(new Map());
  const [filters, setFilters] = useState<CombineFilter>({});
  // "In at least N lists" — `"all"` is "Only people in every list".
  const [listRule, setListRule] = useState<ListRule>(null);
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  // Unticks the server reported as naming nobody any more (their person's
  // kept row changed) — dropped from `excludedIds` and counted here so the
  // page can say so (/pr-review iter 1+2, Agent A "exclusion drift").
  const [droppedExclusions, setDroppedExclusions] = useState(0);
  const [page, setPage] = useState(1);
  // Included / "Removed by you" — a server-side view of the preview (F4).
  const [view, setView] = useState<NonNullable<CombinePreviewView>>("included");
  // "Remove contacts already in Thrive" (round 2, sub-task R2-B) — on by
  // default, scoped to the target search (the plan's own default). The
  // People-lists card's Compare with Thrive section is scrolled to by the
  // funnel's "Not checked in Thrive" line — see `compareSectionRef`.
  const [removeInThrive, setRemoveInThrive] = useState<"off" | "search" | "any">("search");
  const [sort, setSort] = useState<ContactsTableSort | null>(null);
  const compareSectionRef = useRef<HTMLDivElement | null>(null);

  // The target search, set ONLY by the recruiter (epic
  // 2026-10-01-combine-stability-perf, sub-task 02): picking one in the
  // field, or "Use this search" on the lists' shared search. Nothing the
  // preview answers writes it, so adding a list from another search never
  // clears it and no request carries a search nobody chose. Every request
  // (preview and Generate) and every Thrive check uses this, never the
  // suggestion: nothing reaches Thrive before it is set.
  const [target, setTarget] = useState<TargetSearch | null>(null);
  const searchId = target?.id ?? null;
  const searchName = target?.name ?? null;
  const targetLabel = target ? targetLabelOf(target) : null;

  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);

  const sourceIds = useMemo(() => [...sources.keys()], [sources]);
  const sourceList = useMemo(() => [...sources.values()], [sources]);
  const sourceTotalContacts = useMemo(
    () => sourceList.reduce((sum, s) => sum + s.total_contacts, 0),
    [sourceList]
  );
  const sourceTitleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of sourceList) map.set(s.import_id, displayTitle(s.filename));
    return map;
  }, [sourceList]);
  // The "Candidacy stage" filter's view of the selected lists (05).
  const stageSources = useMemo<StageSource[]>(
    () =>
      sourceList.map((s) => ({
        id: s.import_id,
        title: displayTitle(s.filename),
        hasCandidacyData: s.has_candidacy_data,
        people: s.total_contacts,
      })),
    [sourceList]
  );

  function toggleSource(item: ImportSummary) {
    // Refused at the cap with the warning toast and no state change.
    // `ImportPicker` refuses the toggle itself and calls `onMaxReached`
    // (wired below to the same toast); this guard covers any other caller.
    if (!sources.has(item.import_id) && sources.size >= MAX_COMBINE_SOURCES) {
      notifyMaxSources();
      return;
    }
    setSources((prev) => {
      const next = new Map(prev);
      if (next.has(item.import_id)) next.delete(item.import_id);
      else if (next.size < MAX_COMBINE_SOURCES) next.set(item.import_id, item);
      return next;
    });
    // An untick names a merge WINNER's row id, and a different set of lists
    // can merge a person under a different winner (or drop their list) — so
    // unticks do not survive a source change and cannot keep using up the
    // 100-exclusion cap for rows no longer in the preview (F5).
    setExcludedIds(new Set());
    setDroppedExclusions(0);
    setPage(1);
  }

  function toggleExcluded(contactId: string) {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
        return next;
      }
      // Growing the set is capped; shrinking it (re-including someone) never is.
      if (next.size >= MAX_EXCLUSIONS) return next;
      next.add(contactId);
      return next;
    });
  }

  function resetAll() {
    setSources(new Map());
    setFilters({});
    setListRule(null);
    setExcludedIds(new Set());
    setDroppedExclusions(0);
    setPage(1);
    setView("included");
    setTarget(null);
    setTitle("");
    setTitleTouched(false);
    setRemoveInThrive("search");
    setSort(null);
    setResetKey((k) => k + 1);
  }

  // Every recipe change below can shrink the list being paged, so each one
  // goes back to page 1 — a later page could otherwise come back empty (F2).
  function changeFilters(next: CombineFilter) {
    setFilters(next);
    setPage(1);
  }

  function changeListRule(next: ListRule) {
    setListRule(next);
    setPage(1);
  }

  function changeRemoveInThrive(next: "off" | "search" | "any") {
    setRemoveInThrive(next);
    setPage(1);
  }

  function changeView(next: NonNullable<CombinePreviewView>) {
    setView(next);
    setPage(1);
  }

  function handleSortChange(key: string) {
    setSort((prev) => {
      if (prev && prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: "asc" };
    });
    setPage(1);
  }

  // "In at least N lists" means nothing with one list — sent (and shown)
  // only with 2+ (F7); the server normalizes it the same way. N is clamped
  // to the lists selected now, so removing a list never sends an N the API
  // would refuse (`listRule.ts`).
  const appliedListRule = useMemo(
    () => applyListRule(listRule, sourceIds.length),
    [listRule, sourceIds.length]
  );
  const { requireAllSources: effectiveRequireAllSources, minSources } = appliedListRule;

  const recipe: CombinePreviewRequest = useMemo(
    () => ({
      source_import_ids: sourceIds,
      filters,
      require_all_sources: effectiveRequireAllSources,
      min_sources: minSources,
      excluded_contact_ids: [...excludedIds],
      page,
      page_size: PREVIEW_PAGE_SIZE,
      view,
      remove_in_thrive: removeInThrive,
      search_id_thrive: searchId,
      sort_key: sort ? toSortKey(sort.key) : "name",
      sort_dir: sort?.dir ?? "asc",
    }),
    [
      sourceIds,
      filters,
      effectiveRequireAllSources,
      minSources,
      excludedIds,
      page,
      view,
      removeInThrive,
      searchId,
      sort,
    ]
  );

  // Over the byte budget the request is never sent: past 8192 bytes the
  // edge answers an unreadable 403, and the API refuses anything over the
  // budget anyway (/pr-review iter 2, Agent A §6).
  const previewTooLarge = exceedsBodyBudget(recipe, maxBodyBytes);

  const {
    query: previewQuery,
    preview,
    answered: previewAnswered,
    isUpdating: previewUpdating,
    isFetching: previewFetching,
  } = useCombinePreview({
    client: activeClient,
    recipe,
    enabled: sourceIds.length >= MIN_SOURCES && !previewTooLarge,
  });

  // An untick the server says names nobody any more is dropped (not sent
  // again) and counted for the notice below. Counted HERE, from the unticks
  // this render holds, not inside the `setExcludedIds` updater: StrictMode
  // runs updaters twice, which counted every drop twice (/pr-review iter 3,
  // Agent A §3). Once they are dropped the effect finds none left and stops.
  const staleExcluded = preview?.stale_excluded_contact_ids;
  useEffect(() => {
    if (!staleExcluded || staleExcluded.length === 0) return;
    const stale = new Set(staleExcluded);
    const dropped = [...excludedIds].filter((id) => stale.has(id)).length;
    // THIS return is what ends the effect. `excludedIds` is a dependency and
    // the update below changes it, so the effect runs once more; that run
    // finds no stale untick left and stops here. Remove this guard and the
    // effect re-runs on its own update forever (/pr-review iter 4, Agent C m3).
    if (dropped === 0) return;
    setExcludedIds((prev) => new Set([...prev].filter((id) => !stale.has(id))));
    setDroppedExclusions((n) => n + dropped);
  }, [staleExcluded, excludedIds]);

  // Restoring rows on "Removed by you" shrinks that view; a page past its
  // new end steps back to the last page that still has rows.
  useEffect(() => {
    if (!preview || preview.total === 0) return;
    const lastPage = Math.ceil(preview.total / preview.page_size);
    if (preview.page > lastPage) setPage(lastPage);
  }, [preview]);

  // Compare with Thrive (round 2, sub-task R2-B; automatic since task
  // 2026-09-24-combine-more-filters, batch 2) — `preview.sources` names
  // which SELECTED sources still need a run for the ACTIVE mode. Settling
  // (`onSettled`) invalidates the preview (a fresh recipe read picks up the
  // new Compare state) and the firm-wide imports list (so `ImportPicker`'s
  // own "Compared · Nd" chip, sourced from `ImportSummary.compare`, updates
  // without a page reload).
  //
  // Epic 2026-09-28-combine-perf-batch-1, sub-task 02 (item 2): COALESCED,
  // not once per list. An auto-Compare batch of up to `MAX_COMBINE_SOURCES`
  // (20) lists used to cost one refetch + one invalidate PER list as each
  // settled. `useCompareSources`'s batched poll loop (item 1) turns "several
  // lists settle together" into the COMMON case: one tick's response maps
  // several ids' terminal transitions in a single synchronous pass, firing
  // `onSettled` once per id, back to back, with no `await` between them. A
  // MICROTASK — not a `setTimeout` debounce — is what coalesces exactly that
  // pass: the first `onSettled` in the pass schedules the refetch, every
  // later one in the SAME pass is a no-op (the flag guards it), and the
  // scheduled work runs the moment that synchronous pass ends — before the
  // next paint, before any `setTimeout`, and crucially before this hook's
  // OWN next tick. A list that settles alone gets the identical treatment:
  // one `onSettled` call, one scheduled microtask, refetching immediately —
  // never held waiting for a batch that never comes, and never introducing
  // an artificial window where nothing is fetching (PR #965 iter 5, Agent C
  // N5's "the refetch is in flight the moment the run ends" invariant, which
  // a real `setTimeout` debounce would have reopened).
  const settleFlushScheduledRef = useRef(false);
  const compareSources = useCompareSources({
    client: activeClient,
    onSettled: () => {
      if (settleFlushScheduledRef.current) return;
      settleFlushScheduledRef.current = true;
      queueMicrotask(() => {
        settleFlushScheduledRef.current = false;
        void previewQuery.refetch();
        void queryClient.invalidateQueries({ queryKey: ["imports", "list"] });
      });
    },
  });
  // The per-list states of a preview that answered the CURRENT target and
  // mode, else none (sub-task 02): what a list says comes from the server's
  // answer for this target, never from the previous target's answer still
  // on screen as a placeholder (a list then reads `evaluating`). A list's
  // state does not depend on the other lists, so a preview for another set
  // of lists with the same target and mode still says the right thing about
  // the lists it covers: adding a list never changes the others' state.
  const statesForTarget = useMemo(
    () =>
      previewAnswered &&
      (previewAnswered.search_id_thrive ?? null) === searchId &&
      (previewAnswered.remove_in_thrive ?? "search") === removeInThrive
        ? (preview?.sources ?? [])
        : [],
    [previewAnswered, searchId, removeInThrive, preview?.sources]
  );
  // The preview on screen answered the current target under ANOTHER removal
  // mode: a placeholder while the new mode's answer is read. The target
  // checks do not depend on the mode, so the wait on them is not over
  // (/pr-review iter 5 on PR #993, Agent A N11): the clock below holds still.
  const modePlaceholder =
    previewAnswered != null &&
    (previewAnswered.search_id_thrive ?? null) === searchId &&
    (previewAnswered.remove_in_thrive ?? "search") !== removeInThrive;
  // The target and mode: a list is started once per value (`useAutoCompare`).
  const compareScopeKey = `${removeInThrive}|${searchId ?? ""}`;
  // Which list the page started under which scope, so `compareRows` can tell
  // a list started for THIS target (`unchecked` if still not checked) from
  // one started for another target (`pending` again).
  const [startedScope, setStartedScope] = useState<ReadonlyMap<string, string>>(new Map());
  const startedIds = useMemo(
    () =>
      new Set([...startedScope].filter(([, scope]) => scope === compareScopeKey).map(([id]) => id)),
    [startedScope, compareScopeKey]
  );
  // Derived from the lists selected NOW (`sourceList`), never from a
  // previous selection's preview (F1) — see `compareRows`.
  const compareRowList = useMemo(
    () =>
      compareRows({
        sources: sourceList,
        states: statesForTarget,
        mode: removeInThrive,
        targetSearchId: searchId,
        progress: compareSources.progress,
        startedIds,
      }),
    [sourceList, statesForTarget, removeInThrive, searchId, compareSources.progress, startedIds]
  );
  // The lists this page saw checking (`target_checking` / `pending`) under
  // the current target and mode, among the lists selected now: a list that
  // only needed its check against the target runs no Compare here, and
  // without this the card vanished instead of reading "All selected lists
  // checked" (combine-followups 04). Advanced in an effect — never written
  // during render — and pruned to the selection, reset on another target or
  // mode.
  const [seenCheckingState, setSeenCheckingState] = useState<SeenChecking>(NO_SEEN_CHECKING);
  useEffect(() => {
    setSeenCheckingState((prev) =>
      nextSeenChecking(prev, compareScopeKey, compareRowList, sourceIds)
    );
  }, [compareScopeKey, compareRowList, sourceIds]);
  const seenChecking = idsSeenChecking(seenCheckingState, compareScopeKey);
  // No button: the lists that need a run are compared by themselves once
  // the selection has been still for a moment (`useAutoCompare`) — and only
  // once a target is confirmed (`compareRows` has no `pending` row before).
  const autoIds = autoCompareIds(compareRowList, compareSources.progress);
  // The preview on screen answers the current recipe, and no read is in
  // flight (a refetch after a Compare is about to change it).
  const previewReady = Boolean(preview) && !previewUpdating && !previewFetching;
  // The lists, mode and target: what decides which lists are checked.
  const compareSelectionKey = `${compareScopeKey}|${sourceIds.join(",")}`;
  // The Compare started BY THE PAGE skips the per-row Thrive photo phase (up to
  // ~25 s a run) — the recruiter is waiting on the preview. The "Try again" /
  // "Compare again" buttons below call `compareSources.start` directly: a click
  // is a Compare a person asked for, and resolves the photos as any other.
  const startCompareSources = compareSources.start;
  const markStarted = useCallback(
    (importIds: readonly string[]) =>
      setStartedScope((prev) => {
        const next = new Map(prev);
        for (const id of importIds) next.set(id, compareScopeKey);
        return next;
      }),
    [compareScopeKey]
  );
  const startCompareWithoutRowAvatars = useCallback(
    (importIds: readonly string[]) => {
      markStarted(importIds);
      startCompareSources(importIds, { skipRowAvatars: true });
    },
    [markStarted, startCompareSources]
  );
  useAutoCompare({
    selectionKey: compareSelectionKey,
    scopeKey: compareScopeKey,
    startIds: autoIds.start,
    followIds: autoIds.follow,
    ready: previewReady,
    start: startCompareWithoutRowAvatars,
    follow: compareSources.track,
    delayMs: autoCompareDelayMs,
  });
  const compareHasRun = (preview?.sources ?? []).some((s) => s.checked && sources.has(s.import_id));
  // A list about to be compared (`pending`) holds Generate like a running
  // one: generating in that moment would skip the removal it is about to get.
  const compareInFlight =
    removeInThrive !== "off" &&
    (anyCompareInFlight(sourceList, preview?.sources ?? [], compareSources.progress) ||
      autoIds.start.length > 0);
  // The picker's chip says "Comparing…" for exactly the lists the card
  // does (PR #962 follow-up F8).
  const comparingIds = useMemo(
    () => new Set(compareRowList.filter((r) => r.kind === "comparing").map((r) => r.importId)),
    [compareRowList]
  );
  const failedCount = compareRowList.filter((r) => r.kind === "failed").length;
  // A list checked against the target search in the background: read the
  // preview again until the check settles (whatever the removal mode — the
  // new list inherits it). A failed check with a retry left
  // (`membership_retry_at`) is read again at a slower pace, and near its
  // retry time; one with none left, or that only a new Compare can heal, is
  // not read again (nothing would change). Only from a preview for the
  // current target (`statesForTarget`).
  const targetChecksPending = pendingTargetChecks(sourceList, statesForTarget);
  const targetCheckRetrying = anyTargetCheckRetrying(sourceList, statesForTarget);
  const nextRetryAt = nextTargetCheckRetryAt(sourceList, statesForTarget);
  const pollMs =
    targetChecksPending > 0
      ? targetCheckPollMs
      : targetCheckRetrying
        ? targetCheckRetryPollMs
        : null;
  const refetchPreviewRef = useRef(previewQuery.refetch);
  refetchPreviewRef.current = previewQuery.refetch;
  // The `since` of the clock the slow note fired for: the note is shown only
  // while that clock is the current one, so a reset clears it by itself.
  const [slowNoteSince, setSlowNoteSince] = useState<number | null>(null);
  // When the wait on the CURRENT set of checks began. Keyed to the lists
  // being waited on (pending, or failed and read again) and the target — not
  // the removal mode: the check does not depend on it. Kept apart from
  // `pollMs`: a retrying failure flips the interval without starting a new
  // wait (/pr-review iter 2, Agent C N3). A list entering the set (a new list
  // added) or a failed check claimed again (a timer retry, or a late worker's
  // adopt of a `worker_timeout` one) starts a new wait: the
  // clock must not outlive the check it times (/pr-review iter 3, Agent A N6
  // / Agent C R1).
  const pendingClock = useRef<{ key: string; since: number } | null>(null);
  const reclaimEpoch = useRef(0);
  const failedBefore = useRef<ReadonlySet<string>>(new Set());
  const activeIds = activeTargetCheckIds(sourceList, statesForTarget);
  const failedNow = failedCheckIds(sourceList, statesForTarget);
  const pendingNow = statesForTarget
    .filter((st) => st.membership_check === "pending")
    .map((st) => st.import_id);
  // Restart only when an id is ADDED to the set: a list leaving it (one of
  // several checks settling) must not reset the pace of the ones still waiting
  // (/pr-review iter 4, Agent A N9 / Agent C C4-1). Nothing moves while the
  // preview on screen is a mode placeholder (`statesForTarget` is empty then).
  const prevActiveIds = useRef<ReadonlySet<string>>(new Set());
  const addedEpoch = useRef(0);
  if (!modePlaceholder) {
    if (pendingNow.some((id) => failedBefore.current.has(id))) reclaimEpoch.current += 1;
    failedBefore.current = new Set(failedNow);
    if (activeIds.some((id) => !prevActiveIds.current.has(id))) addedEpoch.current += 1;
    prevActiveIds.current = new Set(activeIds);
  }
  const pendingKey =
    pollMs != null ? `${target?.id ?? ""}|${reclaimEpoch.current}|${addedEpoch.current}` : null;
  if (pendingKey == null) {
    if (!modePlaceholder) pendingClock.current = null;
  } else if (pendingClock.current?.key !== pendingKey) {
    pendingClock.current = { key: pendingKey, since: Date.now() };
  }
  const slowNote = targetChecksPending > 0 && slowNoteSince === (pendingClock.current?.since ?? -1);
  const clockSince = pendingClock.current?.since ?? null;
  const checksArePending = targetChecksPending > 0;
  useEffect(() => {
    if (pollMs == null) return;
    // Back off, then slow down — never stop: a pending check settles only
    // when a request arrives to apply the server's cap. A hidden tab reads
    // nothing and resumes (at once) when it is visible again.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const hidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";
    const schedule = () => {
      clearTimeout(timer);
      let delay = pollMs;
      if (clockSince != null) {
        const clock = { since: clockSince };
        const elapsed = Date.now() - clock.since;
        if (checksArePending) {
          const pastNote = elapsed >= targetCheckSlowNoteAfterMs;
          if (pastNote) setSlowNoteSince(clock.since);
          const paced =
            elapsed >= targetCheckSlowAfterMs
              ? targetCheckSlowPollMs
              : elapsed >= targetCheckBackoffAfterMs
                ? targetCheckBackoffPollMs
                : pollMs;
          delay = Math.max(pollMs, paced);
          // Wake exactly when the note is due, whatever the pace.
          if (!pastNote) delay = Math.min(delay, targetCheckSlowNoteAfterMs - elapsed);
        } else {
          const paced =
            elapsed >= targetCheckRetrySlowAfterMs
              ? targetCheckRetrySlowPollMs
              : elapsed >= targetCheckRetryBackoffAfterMs
                ? targetCheckRetryBackoffPollMs
                : pollMs;
          delay = Math.max(pollMs, paced);
          // Wake just past the earliest retry, never later than the pace
          // and never sooner than `pollMs`.
          if (nextRetryAt != null) {
            const untilRetry = nextRetryAt - Date.now() + TARGET_CHECK_RETRY_SLACK_MS;
            delay = Math.min(delay, Math.max(pollMs, untilRetry));
          }
        }
      }
      timer = setTimeout(() => {
        if (hidden()) return; // resumed by `onVisible`
        // `cancelRefetch: false`: a read still in flight is not cut short by
        // the next tick (a cold preview can take longer than the interval).
        void refetchPreviewRef.current({ cancelRefetch: false });
        schedule();
      }, delay);
    };
    const onVisible = () => {
      if (hidden()) return;
      void refetchPreviewRef.current({ cancelRefetch: false });
      schedule();
    };
    document.addEventListener("visibilitychange", onVisible);
    schedule();
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [
    pollMs,
    checksArePending,
    clockSince,
    nextRetryAt,
    targetCheckBackoffAfterMs,
    targetCheckBackoffPollMs,
    targetCheckSlowAfterMs,
    targetCheckSlowPollMs,
    targetCheckSlowNoteAfterMs,
    targetCheckRetryBackoffAfterMs,
    targetCheckRetryBackoffPollMs,
    targetCheckRetrySlowAfterMs,
    targetCheckRetrySlowPollMs,
  ]);
  // A re-read of the CURRENT recipe only earns the "Updating preview…" label
  // once it has run past a short delay (/pr-review iter 1, Agent C M4).
  const [slowRefetch, setSlowRefetch] = useState(false);
  useEffect(() => {
    if (!previewFetching) {
      setSlowRefetch(false);
      return;
    }
    const t = setTimeout(() => setSlowRefetch(true), updatingPreviewDelayMs);
    return () => clearTimeout(t);
  }, [previewFetching, updatingPreviewDelayMs]);
  const notCheckedCount = compareRowList.filter(
    (r) => r.kind === "no_target" || r.kind === "target_check_failed" || r.kind === "unchecked"
  ).length;
  const noTargetCount = compareRowList.filter((r) => r.kind === "no_target").length;
  // Checked lists over selected lists: the card's progress (no run %).
  const progress = checkProgress(compareRowList, sourceList);
  const checksInProgress = compareRowList.some(
    (r) =>
      r.kind === "evaluating" ||
      r.kind === "pending" ||
      r.kind === "comparing" ||
      r.kind === "target_checking"
  );
  // Every selected list checked after a run seen here: the card stays with
  // no rows so its live heading can say so (`checkedAfterCompare`).
  const showCompareCard =
    compareRowList.length > 0 ||
    (removeInThrive !== "off" &&
      checkedAfterCompare(sourceList, statesForTarget, compareSources.progress, seenChecking));
  // The selection key whose ready preview last read "All selected lists
  // checked", so a filter edit's refetch does not take it back
  // (`nextAllCheckedKey`).
  const [allCheckedKey, setAllCheckedKey] = useState<string | null>(null);
  // From the rows and the preview on screen now, never from what it said
  // before (`compareHeading`).
  const compareCardHeading = compareHeading({
    rows: compareRowList,
    targetLabel,
    sources: sourceList,
    states: statesForTarget,
    progress: compareSources.progress,
    previewReady,
    allCheckedHeld: allCheckedKey === compareSelectionKey,
    seenChecking,
  });
  useEffect(() => {
    setAllCheckedKey((prev) =>
      nextAllCheckedKey(prev, {
        previewReady,
        heading: compareCardHeading,
        selectionKey: compareSelectionKey,
      })
    );
  }, [previewReady, compareCardHeading, compareSelectionKey]);

  // The last successful preview's limit, kept around for the 413 branch
  // below: the server 413s BEFORE returning a preview body, so `preview`
  // itself is `undefined` on that error and cannot supply the number
  // (iter-1 review, Agent A §2 item 1 / finding "413 surfaced truthfully").
  const lastMaxSourceContactsRef = useRef<number | null>(null);
  useEffect(() => {
    if (preview) lastMaxSourceContactsRef.current = preview.max_source_contacts;
  }, [preview]);

  // The search every selected list shares, offered as a SUGGESTION until
  // the recruiter confirms it ("Use this search"): an inherited search is
  // not a confirmation (sub-task 02, R1). Computed CLIENT-SIDE from the
  // selected `ImportSummary` rows (each carries `search_id_thrive` /
  // `search_name`), the same rule as the server's `inherited_search` (every
  // list linked, all to one search), so it shows at once rather than when a
  // cold preview lands (F6). The name falls back to the preview's.
  const suggestion = useMemo<TargetSearch | null>(() => {
    if (sourceList.length === 0) return null;
    const ids = new Set(sourceList.map((s) => s.search_id_thrive ?? null));
    const [only] = ids;
    if (ids.size !== 1 || !only) return null;
    const named = sourceList.find((s) => s.search_name)?.search_name ?? null;
    const inherited = preview?.inherited_search;
    return {
      id: only,
      name: named ?? (inherited?.id_thrive === only ? (inherited.name ?? null) : null),
    };
  }, [sourceList, preview?.inherited_search]);
  const offerSuggestion = target == null && suggestion != null;

  // The target search decides who "This search" removes, so a change goes
  // back to page 1 too (F2).
  function changeSearch(nextId: string | null, nextName: string | null) {
    setTarget(nextId ? { id: nextId, name: nextName } : null);
    if (nextId !== searchId) setPage(1);
  }

  function confirmSuggestion() {
    if (suggestion) changeSearch(suggestion.id, suggestion.name);
  }

  // "N searches — pick one" / "lists have no search — pick one", from the
  // selected `ImportSummary` rows, mirroring the mock's
  // `combineSelectedSearchKeys()` / `renderCombineTarget`. None once a
  // target is set, nor while the lists' shared search is offered (its own
  // neutral line says what to do, F6).
  const searchHint = useMemo(() => {
    if (sourceIds.length === 0 || target || suggestion) return null;
    const distinctSearchIds = new Set<string>();
    for (const s of sourceList) {
      if (s.search_id_thrive) distinctSearchIds.add(s.search_id_thrive);
    }
    return distinctSearchIds.size > 1
      ? `${distinctSearchIds.size} searches — pick one`
      : "lists have no search — pick one";
  }, [sourceIds.length, target, suggestion, sourceList]);

  // A single source list is FILTERING it, not combining several (product
  // decision, 2026-09-23) — the suggested title says so. The suggestion's
  // name counts here: a title is only text, it checks nothing.
  const titleSearchName = searchName ?? (offerSuggestion ? (suggestion?.name ?? null) : null);
  const suggestedTitle = titleSearchName
    ? `${sourceIds.length === 1 ? "Filtered" : "Combined"} · ${titleSearchName} · ${todayLabel()}`
    : "";
  useEffect(() => {
    if (titleTouched) return;
    setTitle(suggestedTitle);
  }, [suggestedTitle, titleTouched]);

  function changeTitle(next: string) {
    setTitleTouched(true);
    setTitle(next);
  }

  // From EVERY source row the preview read (most frequent first), not only
  // the page on screen (/pr-review iter 1+2, Agent A).
  const companyOptions = preview?.filter_suggestions?.company ?? [];
  const locationOptions = preview?.filter_suggestions?.location ?? [];

  // Every filter that can drop someone into `filtered_out` (not the list
  // rule, which counts in `not_in_enough_lists`).
  const filtersActive = Boolean(
    filters.title ||
      filters.company ||
      filters.location ||
      filters.past_company ||
      filters.past_title ||
      filters.keyword ||
      filters.months_in_role ||
      filters.connection_degrees?.length ||
      filters.has_email ||
      filters.candidacy_stages?.length
  );

  const generate = useCombineGenerate({ client: activeClient });

  // Exactly what `handleGenerate` posts — measured against the byte budget.
  const generateBody = useMemo(
    () => ({
      source_import_ids: sourceIds,
      filters,
      require_all_sources: effectiveRequireAllSources,
      min_sources: minSources,
      excluded_contact_ids: [...excludedIds],
      title: title.trim(),
      search_id_thrive: searchId ?? "",
      // Generate applies the SAME "already in Thrive" rule the preview
      // showed — the server pins which sources count as compared on the
      // recipe, and `combine_worker` removes from exactly those.
      remove_in_thrive: removeInThrive,
    }),
    [
      sourceIds,
      filters,
      effectiveRequireAllSources,
      minSources,
      excludedIds,
      title,
      searchId,
      removeInThrive,
    ]
  );
  // Measured with the `request_id` `useCombineGenerate` adds on submit.
  const generateTooLarge = exceedsBodyBudget(
    { ...generateBody, request_id: REQUEST_ID_PLACEHOLDER },
    maxBodyBytes
  );

  const disabledReason = useMemo(() => {
    if (sourceIds.length < MIN_SOURCES) return "Pick at least 1 people list.";
    if (previewTooLarge || generateTooLarge) return REQUEST_TOO_LARGE;
    if (!target) return offerSuggestion ? "Confirm the target search." : "Pick a target search.";
    if (!preview) return "Waiting for the preview…";
    // What is on screen must be the answer to the recipe Generate will send,
    // including the re-read after a Compare that just ended.
    if (previewUpdating || (slowRefetch && compareRowList.some((r) => r.kind === "compared")))
      return "Updating preview…";
    // Every selected list checked against the target, or Generate waits and
    // says why (sub-task 02, R5): lists still checking, a Compare that
    // failed, a list a run here left unchecked. A check that FAILED does not
    // hold Generate (`generateNote` says what it means).
    if (slowNote) return CHECK_TAKING_LONG;
    const hold = generateHold(compareRowList, targetLabelOf(target));
    if (hold) return hold;
    // Under "off" no list has a row, but the server still refuses (409
    // `target_check_pending`) while a check the new list inherits runs.
    if (targetChecksPending > 0)
      return stillCheckingCopy(targetChecksPending, targetLabelOf(target));
    // The server refuses (409) while a source's Compare can still change who
    // the removal drops.
    if (compareInFlight) return WAITING_FOR_COMPARE;
    // NOTE: no `source_contacts_total > max_source_contacts` arm here — the
    // server 413s BEFORE returning a preview body once the sources are over
    // the limit, so `preview` is `undefined` whenever this would have been
    // true and the arm was dead code. The over-limit copy lives on the 413
    // branch of `previewErrorMessage` below instead (iter-1 review, Agent A
    // §2 item 1).
    if (excludedIds.size > MAX_EXCLUSIONS)
      return `You can exclude at most ${MAX_EXCLUSIONS} people.`;
    if (!title.trim()) return "Give the new list a title.";
    if (preview.funnel.result === 0) {
      // The Thrive removal alone can empty the result (more often since a
      // list compared against another search is checked too, epic
      // 2026-09-28-combine-perf-batch-1 sub-task 04): the filters are not
      // what to adjust then.
      const { in_thrive, filtered_out, excluded } = preview.funnel;
      if (in_thrive > 0 && filtered_out === 0 && excluded === 0) {
        return "Everyone left is already in Thrive — nothing to generate.";
      }
      return "No contacts match — adjust your filters.";
    }
    return null;
  }, [
    sourceIds.length,
    previewTooLarge,
    generateTooLarge,
    target,
    offerSuggestion,
    preview,
    previewUpdating,
    slowRefetch,
    slowNote,
    compareRowList,
    targetChecksPending,
    compareInFlight,
    excludedIds.size,
    title,
  ]);

  // Beside an enabled Generate: a list whose check against the target
  // failed stays unchecked, and Generate says so rather than waiting for a
  // retry that may never come (the server accepts it).
  const generateNoteText = useMemo(
    () =>
      target
        ? generateNote(
            compareRowList,
            targetLabelOf(target),
            (id) => sourceTitleById.get(id) ?? "a list"
          )
        : null,
    [compareRowList, target, sourceTitleById]
  );

  /**
   * Truthful copy for the preview error branch (iter-1 review, Agent A §2
   * item 1 / "413 surfaced truthfully"). Before this, EVERY preview failure
   * rendered the same generic "Couldn't load the preview" line, which hid a
   * 413 the client's own docstring (`imports.client.ts`'s `combinePreview`)
   * already promised callers would read.
   */
  const previewErrorMessage = useMemo(() => {
    const err = previewQuery.error;
    if (isEdgeRejection(err)) return REQUEST_TOO_LARGE;
    if (!(err instanceof ImportsApiError)) {
      return "Couldn't load the preview. Please try again.";
    }
    if (err.status === 413) {
      // The limit from the 413's own structured detail; else the last
      // successful preview's; else no number (never the message's first
      // number — that is the TOTAL, /pr-review iter 2, Agent A §3).
      return overLimitCopy(overLimitMax(err) ?? lastMaxSourceContactsRef.current);
    }
    if (err.status === 503) return PREVIEW_BUSY;
    if (err.status === 422) {
      if (isSearchError(err)) return "Pick a search.";
      const text = validationText(err);
      return text
        ? `The preview request was rejected: ${text}`
        : "The preview request was rejected.";
    }
    return "Couldn't load the preview. Please try again.";
  }, [previewQuery.error]);

  // Set synchronously on submit: `generate.isPending` only turns true once
  // the mutation observer notifies (a tick later), so a double click could
  // otherwise post the recipe twice and create two lists (F9).
  const submittingRef = useRef(false);

  function handleGenerate() {
    if (disabledReason || !searchId || submittingRef.current || generate.isPending) return;
    submittingRef.current = true;
    generate.mutate(generateBody, {
      onError: (err) => {
        // An untick went stale between the last preview and Generate: re-read
        // the preview, which reports (and so drops) it. Likewise when a list
        // is still being checked against the target search: the preview then
        // shows which, and polls until it settles.
        const code = conflictCode(err);
        if (code === "stale_exclusions" || code === "target_check_pending") {
          void previewQuery.refetch();
        }
      },
      onSettled: () => {
        submittingRef.current = false;
      },
    });
  }

  // A 409 `target_check_pending` is about checks that settle by themselves
  // (the page re-reads the preview and polls them): once Generate has been
  // held for them and is free again, its copy is no longer true, so it goes.
  const generateConflict = conflictCode(generate.error);
  const resetGenerate = generate.reset;
  const heldSinceConflictRef = useRef(false);
  useEffect(() => {
    if (generateConflict !== "target_check_pending") {
      heldSinceConflictRef.current = false;
    } else if (disabledReason != null) {
      heldSinceConflictRef.current = true;
    } else if (heldSinceConflictRef.current) {
      heldSinceConflictRef.current = false;
      resetGenerate();
    }
  }, [generateConflict, disabledReason, resetGenerate]);

  const generateErrorMessage = (() => {
    const err = generate.error;
    if (!err) return null;
    if (isEdgeRejection(err)) return REQUEST_TOO_LARGE;
    // Answer lost, resend failed: the list may exist. Generate again
    // re-sends the same request id, so it cannot make a second one.
    if (err instanceof GenerateUnconfirmedError) return GENERATE_UNCONFIRMED;
    if (!(err instanceof ImportsApiError)) {
      return "Couldn't generate the new list. Please try again.";
    }
    if (err.status === 422) {
      return isSearchError(err) ? "Pick a search before generating." : err.message;
    }
    if (err.status === 413) return overLimitCopy(overLimitMax(err));
    if (err.status === 503) return GENERATE_BUSY;
    if (conflictCode(err) === "target_check_pending") {
      // The server names the lists it is still checking (`import_ids`).
      const ids = err.detailData?.import_ids;
      const lists = Array.isArray(ids) && ids.length > 0 ? ids.length : 1;
      return `${stillCheckingCopy(lists, targetLabel ?? "the target search")} Generate again once ${lists === 1 ? "it is" : "they are"} checked.`;
    }
    // The same Compare-running situation the disabled button covers, reached
    // when a run started elsewhere after this page last looked (B3/F9).
    if (conflictCode(err) === "compare_in_flight") return WAITING_FOR_COMPARE;
    return err.message;
  })();

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-h1 text-text">Combine &amp; Filter</h1>
        <InfoTip content="Build a new people list from existing ones. Source lists are never changed. Your work is kept as a draft until you Generate." />
        <button
          type="button"
          onClick={resetAll}
          data-testid="combine-start-over"
          className="ml-auto text-label text-text-secondary hover:text-text hover:underline"
        >
          Start over
        </button>
      </div>

      <section
        className="rounded-md border border-border bg-surface p-4"
        data-testid="combine-card-sources"
      >
        <CardHeader title="People lists" tip="Pick the people lists to combine.">
          <span className="text-caption text-text-secondary" data-testid="combine-source-tally">
            {sourceIds.length > 0
              ? `${sourceIds.length} ${sourceIds.length === 1 ? "list" : "lists"} · ${sourceTotalContacts.toLocaleString()} ${sourceTotalContacts === 1 ? "contact" : "contacts"}`
              : ""}
          </span>
        </CardHeader>
        <SourcePicker
          key={resetKey}
          client={activeClient}
          meEmail={meEmail}
          selected={sourceList}
          onToggle={toggleSource}
          onMaxReached={notifyMaxSources}
          comparingIds={comparingIds}
        />
        <SelectedListsPanel
          sources={sourceList}
          onRemove={toggleSource}
          states={removeInThrive === "off" ? undefined : statesForTarget}
        />
        {sourceIds.length > 0 ? (
          <div
            className={`mt-3 flex flex-wrap items-center gap-2 rounded-md border p-2 ${
              searchId || offerSuggestion
                ? "border-border bg-bg"
                : "border-warning bg-warning-muted"
            }`}
            data-testid="combine-target-wrap"
          >
            <span
              aria-hidden="true"
              className={`h-2 w-2 shrink-0 rounded-full ${
                searchId || offerSuggestion ? "bg-transparent" : "bg-warning"
              }`}
            />
            <span className="text-label font-semibold text-text">Search</span>
            <InfoTip content="The search the new people list belongs to. Compare, Add to search and the Thrive filter use it. When all lists share one search it is suggested; nothing is checked in Thrive until you confirm it or pick another." />
            <div className="min-w-56 flex-1">
              <TargetSearchStep
                client={activeClient}
                value={searchId}
                valueName={searchName}
                onChange={changeSearch}
              />
            </div>
            {searchHint ? (
              <span className="text-caption text-text-muted" data-testid="combine-target-hint">
                {searchHint}
              </span>
            ) : null}
            {offerSuggestion && suggestion ? (
              <div
                className="flex w-full flex-wrap items-center gap-2 text-caption text-text-secondary"
                data-testid="combine-target-suggestion"
              >
                <span>
                  All selected lists are in{" "}
                  <span className="font-semibold text-text">{targetLabelOf(suggestion)}</span>.
                  Nothing is checked in Thrive until you confirm the search.
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={confirmSuggestion}
                  data-testid="combine-target-confirm"
                >
                  Use this search
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
        {/* Compare with Thrive (round 2, sub-task R2-B; automatic since task
            2026-09-24-combine-more-filters, batch 2) — only while the
            "Remove contacts already in Thrive" toggle wants a check
            (`removeInThrive !== "off"`) AND a selected list is not checked
            for it yet, or was compared on this page. What each list says
            comes from `compareRows` (F6): a run starts by itself where it
            can make the list count, a reason is given otherwise. No button:
            the only clicks are "Try again" on a failed run and "Compare
            again" on a list this page compared that is still not checked
            (`unchecked`). Once every list is checked after a run seen here,
            the card stays with no rows (`checkedAfterCompare`). The heading is a
            polite live region. Progress is lists checked over lists selected
            (`checkProgress`), never a run's percentage (sub-task 02, R6).
            Ref'd so the funnel's own "Not checked in Thrive" line can
            scroll here. */}
        <div ref={compareSectionRef} data-testid="combine-compare-section">
          {showCompareCard ? (
            <div
              className={`mt-3 flex flex-col gap-2 rounded-md border p-2 ${
                failedCount > 0 || notCheckedCount > (offerSuggestion ? noTargetCount : 0)
                  ? "border-warning bg-warning-muted"
                  : "border-border bg-bg"
              }`}
              data-testid="combine-compare-banner"
            >
              <output
                className="block text-label text-text"
                aria-live="polite"
                data-testid="combine-compare-heading"
              >
                {compareCardHeading}
              </output>
              {checksInProgress ? (
                <progress
                  className="h-1.5 w-full overflow-hidden rounded-pill accent-accent"
                  max={progress.total}
                  value={progress.checked}
                  aria-label="Lists checked in Thrive"
                  data-testid="combine-compare-progress"
                />
              ) : null}
              <ul className={`flex-col gap-1 ${compareRowList.length > 0 ? "flex" : "hidden"}`}>
                {compareRowList.map((row) => {
                  const listTitle = sourceTitleById.get(row.importId) ?? row.importId;
                  return (
                    <li
                      key={row.importId}
                      data-testid={`combine-compare-row-${row.importId}`}
                      data-kind={row.kind}
                      className="flex flex-wrap items-center gap-2 text-caption text-text-secondary"
                    >
                      <span className="text-text">{listTitle}</span>
                      <span
                        className={`rounded-pill border px-2 py-0.5 text-caption ${
                          row.kind === "failed"
                            ? "border-error text-error"
                            : "border-border bg-surface text-text-secondary"
                        }`}
                        data-testid={`combine-compare-chip-${row.importId}`}
                      >
                        {compareNote(row, targetLabel, offerSuggestion)}
                      </span>
                      {row.kind === "failed" || row.kind === "unchecked" ? (
                        <button
                          type="button"
                          onClick={() => {
                            markStarted([row.importId]);
                            compareSources.start([row.importId]);
                          }}
                          data-testid={`combine-compare-retry-${row.importId}`}
                          className="text-caption text-accent hover:underline"
                        >
                          {row.kind === "failed" ? "Try again" : "Compare again"}
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      </section>

      <section
        className="rounded-md border border-border bg-surface p-4"
        data-testid="combine-card-filters"
      >
        <CardHeader
          title="Filters"
          tip="Different fields combine with AND. Several values on the same field combine with OR. A row without a value is ignored."
        />
        {/* "Remove contacts already in Thrive" (round 2, sub-task R2-B) — on
            by default, scoped to the target search. The scope segmented
            control is meaningless while the toggle is off, so it disables
            with it rather than hiding (same "show, don't hide" instinct the
            rest of this page follows for its warnings). */}
        <div
          className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-border bg-bg p-2"
          data-testid="combine-remove-in-thrive"
        >
          <label className="flex items-center gap-2 text-label text-text">
            <input
              type="checkbox"
              checked={removeInThrive !== "off"}
              onChange={(e) => changeRemoveInThrive(e.target.checked ? "search" : "off")}
              data-testid="combine-remove-in-thrive-checkbox"
            />
            Remove contacts already in Thrive
          </label>
          <SegmentedControl
            aria-label="Which searches to check"
            data-testid="combine-remove-in-thrive-scope"
            value={removeInThrive === "off" ? "search" : removeInThrive}
            onChange={(v) => changeRemoveInThrive(v)}
            options={[
              {
                value: "search",
                label: "This search",
                testId: "combine-remove-in-thrive-search",
                disabled: removeInThrive === "off",
                title:
                  removeInThrive === "off" ? "Turn on the toggle to choose a scope" : undefined,
              },
              {
                value: "any",
                label: "Any search",
                testId: "combine-remove-in-thrive-any",
                disabled: removeInThrive === "off",
                title:
                  removeInThrive === "off" ? "Turn on the toggle to choose a scope" : undefined,
              },
            ]}
          />
        </div>
        <FilterBuilder
          key={resetKey}
          filters={filters}
          onChange={changeFilters}
          listRule={listRule}
          onListRuleChange={changeListRule}
          sourceCount={sourceIds.length}
          companyOptions={companyOptions}
          locationOptions={locationOptions}
          stageSources={stageSources}
        />
      </section>

      <section
        className="rounded-md border border-border bg-surface p-4"
        data-testid="combine-card-review"
      >
        <CardHeader
          title="Review contacts"
          tip="Untick anyone you don't want. In Excluded you can see why each person was left out and keep them anyway."
        />
        {droppedExclusions > 0 ? (
          <p className="mb-2 text-caption text-warning-ink" data-testid="combine-stale-exclusions">
            {droppedExclusions === 1
              ? "1 untick no longer applies"
              : `${droppedExclusions} unticks no longer apply`}{" "}
            — the list those people came from changed since you unticked them. Check the list and
            untick again if needed.
          </p>
        ) : null}
        {previewTooLarge && sourceIds.length >= MIN_SOURCES ? (
          <p className="text-body text-error" data-testid="combine-preview-error">
            {REQUEST_TOO_LARGE}
          </p>
        ) : previewQuery.isError ? (
          <p className="text-body text-error" data-testid="combine-preview-error">
            {previewErrorMessage}
          </p>
        ) : preview ? (
          <PreviewTable
            key={resetKey}
            client={activeClient}
            items={preview.items ?? []}
            funnel={preview.funnel}
            page={preview.page}
            pageSize={preview.page_size}
            total={preview.total}
            onPageChange={setPage}
            view={view}
            onViewChange={changeView}
            excludedIds={excludedIds}
            onToggleExcluded={toggleExcluded}
            maxExclusions={MAX_EXCLUSIONS}
            sourceTitleById={sourceTitleById}
            sort={sort}
            onSortChange={handleSortChange}
            compareHasRun={compareHasRun}
          />
        ) : (
          <p className="text-body text-text-muted" data-testid="combine-preview-placeholder">
            Pick at least 1 people list to see a preview.
          </p>
        )}
      </section>

      <section
        className="rounded-md border border-accent-border bg-surface p-4"
        data-testid="combine-card-result"
      >
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="text-h3 text-text">Result</h2>
          <span className="ml-auto flex items-center gap-3 text-caption text-text-secondary">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-text-muted" aria-hidden="true" />
              automatic
            </span>
            {/* Orange (warning tokens), not accent — round 2 cosmetics.
                Accent is reserved for the single big result number below. */}
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-warning" aria-hidden="true" />
              your choices
            </span>
          </span>
        </div>
        {preview ? (
          <PreviewFunnel
            funnel={preview.funnel}
            minSources={appliedListRule.n}
            sourceCount={sourceIds.length}
            filtersActive={filtersActive}
            removeInThrive={removeInThrive}
            duplicateGroups={preview.duplicate_groups}
            duplicateGroupsTotal={preview.duplicate_groups_total}
            targetSearchId={searchId}
            targetSearchName={searchName}
            onGoToCompare={() =>
              compareSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
            }
          />
        ) : (
          <p className="text-body text-text-muted">No preview yet.</p>
        )}
      </section>

      <div className="flex flex-wrap items-end gap-3" data-testid="combine-footer">
        <label className="flex min-w-60 max-w-md flex-1 flex-col gap-1.5" htmlFor="combine-title">
          <span className="flex items-center gap-1.5 text-label text-text-secondary">
            People list title
            <InfoTip content="Suggested from the search and date until you edit it." />
          </span>
          <input
            id="combine-title"
            type="text"
            value={title}
            onChange={(e) => changeTitle(e.target.value)}
            data-testid="combine-title-input"
            className="rounded-md border border-border bg-bg px-2 py-1.5 text-body text-text placeholder:text-text-muted focus:border-accent focus:outline-none"
          />
        </label>

        <div className="flex flex-col gap-1">
          <Button
            type="button"
            variant="primary"
            disabled={Boolean(disabledReason) || generate.isPending}
            aria-busy={generate.isPending}
            onClick={handleGenerate}
            data-testid="combine-generate-button"
          >
            {generate.isPending ? "Generating…" : "Generate"}
          </Button>
          {disabledReason ? (
            <p className="text-caption text-text-muted" data-testid="combine-generate-reason">
              {disabledReason}
            </p>
          ) : generateNoteText ? (
            <p className="text-caption text-text-muted" data-testid="combine-generate-note">
              {generateNoteText}
            </p>
          ) : null}
          {generateErrorMessage ? (
            <p className="text-caption text-error" data-testid="combine-generate-error">
              {generateErrorMessage}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
